/**
 * Télé-expertise — the queue of dossiers patients filled in themselves, and what to open next.
 *
 * A third way in, after the connector that pulls and the practitioner who deposits: the patient
 * fills the form on the clinic's own site, before any consultation.
 *
 * The screen opened on two counters — how many dossiers were waiting, and how many of those set a
 * clinical delay. The surgeons asked for them to go, and they were right for a reason worth writing
 * down: a number at the top of a queue is read once and then never again, while the row it sits
 * above has to be read every time. The counters said what the list already shows, and cost the
 * search bar its place at the top of the page.
 *
 * The default order is the backend's and it is theirs (TF1): « en attente d'avis » first, by the
 * form's priority, then **oldest first** — a queue is worked from its bottom, and sorting the
 * waiting by newest is how the dossier that has waited longest becomes the one nobody opens. A date
 * ordering can be picked by name beside it, and it REPLACES that rule rather than nesting in it.
 *
 * Filtering, searching and the facets are all the server's, so a filter never disagrees with the
 * choices offered next to it. The zone chips used to be derived from the rows the screen had just
 * been sent — which are the filtered ones — so picking a zone made every other zone vanish and left
 * no way back but clearing everything.
 *
 * The controls themselves were the second thing to go. Cutting the two counters left eleven
 * identical pills across two rows — status, ordering, priority, zone, strong signal — separated by
 * nothing but a grey word, which is the same wall the surgeons complained about, moved one row down.
 * What a reader changes constantly is on the surface as two segmented controls; what they set once
 * and forget is behind « Filtres », with a count so a forgotten one cannot hide. That is the idiom
 * the dashboard already uses, and matching it is most of what « consistent » means here.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Filter,
  Inbox,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { Trans, useLingui } from "@lingui/macro";
import { toast } from "sonner";
import { Button } from "@app/components/ui/button";
import { Input } from "@app/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@app/components/ui/popover";
import { DossierQueue } from "@app/components/teleexpertise/DossierQueue";
import API from "@app/services/api";
import { cn } from "@app/lib/utils";
import { useViewAsKey } from "@app/services/view-as";
import {
  STATUT_LABELS,
  STATUT_ORDER,
  TRI_LABELS,
  TRI_ORDER,
  type DossierSummary,
  type Tri,
  type TriageStatut,
  type Zone,
} from "@app/utils/teleexpertise";

/** The sentinel for « no filter on this axis ». */
const ALL = "__all__";

/** And for « the queue's own order », which is an ordering and not the absence of one. */
const QUEUE_ORDER = "__queue__";

interface Filters {
  statut: TriageStatut | typeof ALL;
  priorite: string | typeof ALL;
  zone: string | typeof ALL;
  signalFort: boolean | null;
  recherche: string;
  /** `null` is the queue's own order — not an absence of ordering. */
  tri: Tri | null;
}

/**
 * What the screen opens on: the dossiers that still owe an avis.
 *
 * Not « Tous ». A queue is opened to find the next thing to do, and the answered half is a history —
 * useful, and never the first thing. Making the reader filter it away on every visit is asking them
 * to do the same gesture for ever.
 *
 * It stays a FILTER rather than becoming a hidden rule: the « Rendu » and « Tous » buttons sit right
 * there, and « Tout effacer » comes back here rather than to everything, because this is the state
 * the screen means.
 */
const NO_FILTER: Filters = {
  statut: "recu",
  priorite: ALL,
  zone: ALL,
  signalFort: null,
  recherche: "",
  tri: null,
};

export default function TeleExpertisePage() {
  const { i18n, t } = useLingui();
  // Whose platform is on screen: the eye and the speciality picker both change whose queue this is.
  const viewAsKey = useViewAsKey();

  const [dossiers, setDossiers] = useState<DossierSummary[]>([]);
  const [priorites, setPriorites] = useState<{ priorite: string; libelle: string }[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<Filters>(NO_FILTER);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  // Applied on submit rather than on every keystroke: the search runs server-side over the whole
  // clinic pool, and one request per typed character is a request per character.
  const [searchDraft, setSearchDraft] = useState("");

  /** Change a filter and go back to the first page — page three of a new list is nobody's page. */
  const refine = useCallback((update: (previous: Filters) => Filters) => {
    setFilters(update);
    setPage(1);
  }, []);

  const loadQueue = useCallback(
    async (active: Filters, wanted: number) => {
      setLoading(true);
      const response = (await API.get({
        path: "/teleexpertise/dossiers",
        query: {
          statut: active.statut === ALL ? undefined : active.statut,
          priorite: active.priorite === ALL ? undefined : active.priorite,
          zone: active.zone === ALL ? undefined : active.zone,
          signal_fort: active.signalFort === null ? undefined : active.signalFort,
          recherche: active.recherche || undefined,
          tri: active.tri ?? undefined,
          page: wanted,
        },
      })) as {
        ok?: boolean;
        data?: {
          dossiers?: DossierSummary[];
          priorites?: { priorite: string; libelle: string }[];
          zones?: Zone[];
          page?: number;
          pages?: number;
          total?: number;
        };
        error?: string;
      };
      setLoading(false);
      if (!response?.ok) {
        toast.error(response?.error || t`Impossible de charger les dossiers.`);
        return;
      }
      setDossiers(response.data?.dossiers ?? []);
      // The server's page, not the one we asked for: it clamps a page past the end to the last one,
      // and the control has to say where the reader actually is.
      setPage(response.data?.page ?? 1);
      setPages(response.data?.pages ?? 1);
      setTotal(response.data?.total ?? 0);
      if (response.data?.priorites) setPriorites(response.data.priorites);
      // The facets are the POOL's and arrive with every answer, so narrowing the list never
      // narrows the choices.
      if (response.data?.zones) setZones(response.data.zones);
    },
    [t],
  );

  useEffect(() => {
    void loadQueue(filters, page);
  }, [loadQueue, filters, page, viewAsKey]);

  const filtered = useMemo(
    () => JSON.stringify(filters) !== JSON.stringify(NO_FILTER),
    [filters],
  );

  /** How many refinements are hidden behind the button — a filter nobody can see is a filter
      nobody remembers setting, and it is the one that makes a list look empty. */
  const refineCount = useMemo(
    () =>
      (filters.priorite === ALL ? 0 : 1) +
      (filters.zone === ALL ? 0 : 1) +
      (filters.signalFort === null ? 0 : 1),
    [filters],
  );

  return (
    <div className="p-6">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
            <Inbox className="h-6 w-6 text-primary" />
            <Trans>Télé-expertise</Trans>
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            <Trans>
              Les dossiers remplis par les patients sur votre site, à prioriser avant
              consultation.
            </Trans>
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void loadQueue(filters, page)}
          disabled={loading}
          className="border-slate-300 text-slate-700 hover:border-primary/50 hover:text-primary"
        >
          <RefreshCw className={cn("mr-1.5 h-4 w-4", loading && "animate-spin")} />
          <Trans>Actualiser</Trans>
        </Button>
      </header>

      {/* --- the search, then the filters -------------------------------------------- */}
      <form
        className="mb-3 flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          refine((f) => ({ ...f, recherche: searchDraft.trim() }));
        }}
      >
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            placeholder={t`Nom du patient, n° de sécurité sociale, RPPS ou ADELI...`}
            className="pl-9"
          />
        </div>
        <Button type="submit" size="sm" className="bg-primary hover:bg-primary/90">
          <Trans>Rechercher</Trans>
        </Button>
        {filtered && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-slate-500"
            onClick={() => {
              setSearchDraft("");
              refine(() => NO_FILTER);
            }}
          >
            <X className="mr-1 h-3.5 w-3.5" />
            <Trans>Tout effacer</Trans>
          </Button>
        )}
      </form>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Segmented
          options={[
            { value: ALL, label: t`Tous` },
            ...STATUT_ORDER.map((statut) => ({
              value: statut,
              label: i18n._(STATUT_LABELS[statut]),
            })),
          ]}
          value={filters.statut}
          onChange={(value) =>
            refine((f) => ({ ...f, statut: value as Filters["statut"] }))
          }
        />

        {/* Named on screen, never applied in silence: a reader who cannot tell whether they are
            looking at a queue or at a chronology can trust neither. */}
        <Segmented
          options={[
            { value: QUEUE_ORDER, label: t`File d'attente` },
            ...TRI_ORDER.map((tri) => ({ value: tri, label: i18n._(TRI_LABELS[tri]) })),
          ]}
          value={filters.tri ?? QUEUE_ORDER}
          onChange={(value) =>
            refine((f) => ({ ...f, tri: value === QUEUE_ORDER ? null : (value as Tri) }))
          }
        />

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 border-slate-300 text-sm text-slate-600 hover:border-primary/50 hover:text-primary"
            >
              <Filter className="h-3.5 w-3.5" />
              <Trans>Filtres</Trans>
              {refineCount > 0 && (
                <span className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">
                  {refineCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 space-y-4">
            {priorites.length > 0 && (
              <ChipGroup
                title={t`Priorité`}
                options={priorites.map((p) => ({ value: p.priorite, label: p.libelle }))}
                value={filters.priorite}
                onToggle={(value) =>
                  refine((f) => ({ ...f, priorite: f.priorite === value ? ALL : value }))
                }
              />
            )}
            {zones.length > 0 && (
              <ChipGroup
                title={t`Zone`}
                options={zones.map((zone) => ({ value: zone.cle, label: zone.libelle }))}
                value={filters.zone}
                onToggle={(value) =>
                  refine((f) => ({ ...f, zone: f.zone === value ? ALL : value }))
                }
              />
            )}
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-slate-700">
                <Trans>Signaux</Trans>
              </span>
              <FilterChip
                active={filters.signalFort === true}
                onClick={() =>
                  refine((f) => ({ ...f, signalFort: f.signalFort === true ? null : true }))
                }
                label={t`Avec un signal fort`}
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {loading && dossiers.length === 0 ? (
        <p className="text-sm text-slate-500">
          <Trans>Chargement des dossiers...</Trans>
        </p>
      ) : dossiers.length === 0 && !filtered ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
          <Inbox className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-2 text-sm font-medium text-slate-700">
            <Trans>Aucun dossier en attente d'avis.</Trans>
          </p>
          <p className="mt-1 text-xs text-slate-500">
            <Trans>
              Les dossiers déposés depuis le formulaire de votre site apparaîtront ici dans
              les minutes qui suivent leur envoi.
            </Trans>
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3 border-slate-300 text-slate-700 hover:border-primary/50 hover:text-primary"
            onClick={() => refine((f) => ({ ...f, statut: ALL }))}
          >
            <Trans>Voir aussi les avis rendus</Trans>
          </Button>
        </div>
      ) : (
        <>
          <DossierQueue
            dossiers={dossiers}
            hrefFor={(dossier) => `/tele-expertise/${dossier.dossier_id}`}
          />
          {pages > 1 && (
            <div className="mt-3 flex items-center justify-between">
              <p className="text-xs text-slate-500">
                <Trans>
                  Page {page} sur {pages} · {total} dossiers
                </Trans>
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1 border-slate-300 text-slate-700 hover:border-primary/50 hover:text-primary"
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  disabled={page <= 1 || loading}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  <Trans>Précédent</Trans>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1 border-slate-300 text-slate-700 hover:border-primary/50 hover:text-primary"
                  onClick={() => setPage((current) => Math.min(pages, current + 1))}
                  disabled={page >= pages || loading}
                >
                  <Trans>Suivant</Trans>
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** A segmented control: the choices a reader changes constantly, all visible, one of them on. */
function Segmented({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={value === option.value}
          className={cn(
            "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
            value === option.value
              ? "bg-white text-primary shadow-sm"
              : "text-slate-600 hover:text-primary",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** One refinement inside the popover: a title, and chips that toggle off when pressed again. */
function ChipGroup({
  title,
  options,
  value,
  onToggle,
}: {
  title: string;
  options: { value: string; label: string }[];
  value: string;
  onToggle: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-slate-700">{title}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => (
          <FilterChip
            key={option.value}
            active={value === option.value}
            onClick={() => onToggle(option.value)}
            label={option.label}
          />
        ))}
      </div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
        // Outlined rather than filled when on — the shape the temporal dossier uses for exactly
        // this, and a filled chip beside a filled status badge is two solids saying different kinds
        // of thing.
        active
          ? "border-primary bg-primary/5 text-primary"
          : "border-slate-200 bg-white text-slate-600 hover:border-primary/50 hover:text-primary",
      )}
    >
      {label}
    </button>
  );
}
